import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { AddIcon, CheckIcon, EditIcon } from '../../../../components/Icon';

import { equipmentAPI, systemAPI } from '../../../../apis';
import { i18n } from '../../../../language';
import { AutoCompleteSelect } from '../../../../components';
import SearchInput from '../../../../components/SearchInput';
import EditRemark from './EditRemark';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#fff',
  },
  bedStatusSetting: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    lineHeight: '1',
    color: '#2e4469',
  },
  headText: {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '1rem',
  },
  tableContainer: {
    height: '65vh',
  },
  table: {
    position: 'relative',
    minWidth: '45rem',
    '& .MuiTableCell-root': {
      border: 'none',
      fontSize: '1.125rem',
      fontWeight: '500',
      lineHeight: '1.675rem',
      color: '#2e4469',
      padding: '0 1rem ',
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: '0',
      left: '0',
      zIndex: '10',
      '& .MuiTableRow-root': {
        backgroundColor: '#fff',
        textTransform: 'uppercase',
      },
      '& .MuiTableCell-root': {
        fontWeight: 'bold',
        color: '#538fff',
        padding: '1rem',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
    '& .MuiTableBody-root': {
      '& :hover': {
        backgroundColor: '#f5faff',
      },
      '& > .MuiTableRow-root': {
        borderBottom: '1px solid #E6E8E9',
      },
      '& .MuiTableCell-root': {
        height: '3.5rem',
        fontFamily: 'NotoSans-Medium',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
  },
  status: {
    width: '0.625rem',
    height: '0.625rem',
    display: 'inline-block',
    backgroundColor: '#28d2ab',
    borderRadius: '50%',
    marginRight: '0.563rem',
  },
  editBtn: {
    width: '2.25rem',
    height: '2.25rem',
    minWidth: '0',
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    color: '#fff',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  editIcon: {
    fontSize: '1rem',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  tableHead: {
    whiteSpace: 'nowrap',
  },
  add: {
    width: '5.688rem',
    height: '3rem',
    background: '#538fff',
    color: '#fff',
    borderRadius: '0.625rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
  },
  checkIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: '#28d2ab',
  },
  palette: {
    width: '1.375rem',
    height: '1.375rem',
    minWidth: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
  },
}));

const pageSize = 20;
export default function RemarkTab() {
  const classes = useStyles();
  const loginInfo = useSelector((state) => state.loginInfo);
  const [remarkList, setRemarkList] = useState([]);
  const [total, setTotal] = useState(0);

  const [remarksParams, setRemarksParams] = useState({
    pageNo: 1,
    pageSize,
    keyword: '',
    status: 1,
    categoryId: [],
    categoryIds: [],
  });
  const [edit, setEdit] = useState({
    open: false,
    data: {},
  });
  const [categoryList, setCategoryList] = useState([]);
  const tableRef = useRef();

  const list = useMemo(() => {
    return _.map(remarkList, (item) => ({
      ...item,
      status: Boolean(item.status),
    }));
  }, [remarkList]);

  // 打开 edit popup
  const handleOpenEditPopup = useCallback(
    (data) => () => {
      setEdit({
        open: true,
        data,
      });
    },
    []
  );

  // 关闭 edit popup
  const handleCloseEditPopup = () => {
    setEdit({
      open: false,
      data: {},
    });
  };

  // update remark
  const handleUpdateRemark = async (data) => {
    try {
      await systemAPI.updateHospCategoryRemark({
        remarkId: data.remarkId,
        remark: data.remark,
        status: data.status ? 1 : 0,
      });
    } catch (error) {}
  };

  // 添加 remark
  const hanldeCreateRemark = async (data) => {
    try {
      await systemAPI.createHospCategoryRemark({
        hospitalCategoryId: remarksParams?.categoryId?.[0],
        remark: data.remark,
        status: data.status ? 1 : 0,
      });
    } catch (error) {}
  };

  const handleConfirm = async (data) => {
    data?.remarkId ? await handleUpdateRemark(data) : await hanldeCreateRemark(data);
    handleCloseEditPopup();
    const newParams = { ...remarksParams, pageNo: 1, pageSize };
    getHospRemarksList(newParams);
    setRemarksParams(newParams);
  };

  // 翻页
  const turnPages = useCallback(() => {
    if (remarksParams.pageNo * remarksParams.pageSize < total) {
      setRemarksParams((prevVal) => ({
        ...prevVal,
        pageNo: prevVal.pageNo + 1,
      }));
    }
  }, [total, remarksParams]);

  const scrollHandler = useCallback(() => {
    if (tableRef) {
      const { scrollTop, clientHeight, scrollHeight } = tableRef.current;

      if (scrollTop + clientHeight + 1 >= scrollHeight) {
        turnPages();
      }
    }
  }, [turnPages]);

  const getHospRemarksList = async (params) => {
    try {
      const { result } = await systemAPI.getHospCategoryRemarkList(params);
      const currentSelected = _.find(result, (item) => item?.hospitalCategoryId === params?.categoryId?.[0]);
      const { data, count } = currentSelected;
      setTotal(count);
      if (params.pageNo > 1) {
        setRemarkList((prev) => [...prev, ...data]);
      } else {
        setRemarkList(data);
      }
    } catch (error) {}
  };

  const handleChange = (val, type) => {
    setRemarksParams((prevParams) => {
      const params = {
        ...prevParams,
      };
      if (type === 'categoryId') {
        params.categoryId = [val];
        params.categoryIds = [val];
      } else {
        params[type] = val;
      }
      getHospRemarksList(params);
      return params;
    });
  };

  // get Equipment Category list
  const getEquipmentCategorylist = async () => {
    try {
      const { result } = await equipmentAPI.getEquipmentCategory({
        hospitalId: loginInfo.hospitalId,
        wardId: null,
        hospId: null,
      });
      const newCategoryList = _.map(result.categories, (item) => ({ text: item.categoryName, value: item.categoryId }));
      const newRemarkParam = {
        ...remarksParams,
        categoryId: [newCategoryList?.[0]?.value],
        categoryIds: [newCategoryList?.[0]?.value],
      };
      setRemarksParams(newRemarkParam);
      setCategoryList(newCategoryList);
      await getHospRemarksList(newRemarkParam);
    } catch (error) {
      console.log(error);
    }
  };

  // 第一次进来，加载 remark list和 category list
  useEffect(() => {
    getEquipmentCategorylist();
  }, []);

  return (
    <div className={classes.root}>
      <Box mt="1.375rem" mb="2rem">
        <Box width="19.688rem">
          <div className={classes.headText}>Category info</div>
          <AutoCompleteSelect
            id="setting_equipment_remarks_category"
            placeholder="Category"
            value={remarksParams?.categoryId?.[0]}
            itemList={categoryList}
            onChange={(val) => handleChange(val, 'categoryId')}
          />
        </Box>
        <Box mt="1rem">
          <div className={classes.headText}>Remark info</div>
          <Box display="flex" flex="row" alignItems="center" justifyContent="space-between">
            <Box display="flex" flex="row">
              <AutoCompleteSelect
                id="setting_equipment_remarks_status"
                placeholder="Status"
                value={remarksParams?.status}
                itemList={[
                  { text: 'Active', value: 1 },
                  { text: 'InActive', value: 0 },
                  { text: 'All', value: null },
                ]}
                onChange={(val) => handleChange(val, 'status')}
                width="19.688rem"
              />
              <SearchInput
                placeholder="Remark"
                inputID="equipment_remark_search_input"
                buttonID="equipment_remark_button"
                onChange={(val) => handleChange(val, 'keyword')}
                id="setting_equipment_remarks_keyword"
                m="0 0 0 1.5rem"
                height="3.5rem"
              />
            </Box>

            <ButtonBase
              className={classes.add}
              id="bed_setting_add_btn1"
              onClick={handleOpenEditPopup({ remark: '', status: false })}
            >
              <AddIcon style={{ fontSize: '1rem', marginRight: '0.313rem' }} />
              {i18n.t('BEDSTATUS_ADD')}
            </ButtonBase>
          </Box>
        </Box>
      </Box>
      <TableContainer className={classes.tableContainer} ref={tableRef} onScroll={scrollHandler}>
        <Table className={classes.table} aria-label="simple table">
          <TableHead className={classes.tableHead} id="bed_setting_table_head">
            <TableRow>
              <TableCell align="left" style={{ wordBreak: 'break-all' }}>
                {i18n.t('BEDSTATUS_REMARKS_CONTENT')}
              </TableCell>
              <TableCell align="center">{i18n.t('BEDSTATUS_ACTIVE')}</TableCell>
              <TableCell align="right">{i18n.t('BEDSTATUS_EDIT')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody id="bed_setting_table_body">
            {list.map((row, rowIndex) => {
              const { remark, status } = row;
              return (
                <TableRow
                  key={row.remarkId}
                  onDoubleClick={handleOpenEditPopup(row)}
                  id={`bed_setting_table_row${rowIndex}`}
                >
                  <TableCell align="left">{remark}</TableCell>
                  <TableCell align="center">{status ? <CheckIcon className={classes.checkIcon} /> : ''}</TableCell>
                  <TableCell align="right">
                    <ButtonBase
                      className={classes.editBtn}
                      onClick={handleOpenEditPopup(row)}
                      id={`bed_setting_edit_btn${rowIndex}`}
                    >
                      <EditIcon className={classes.editIcon} />
                    </ButtonBase>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {edit.open && <EditRemark handleClose={handleCloseEditPopup} handleConfirm={handleConfirm} data={edit.data} />}
    </div>
  );
}
